import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { sendMagicLink, verifyMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // Check if there's a token to verify
  const token = searchParams.get('token');

  // If already authenticated, redirect
  if (isAuthenticated) {
    navigate('/editor');
    return null;
  }

  const handleSendLink = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const result = await sendMagicLink(email);
      
      // Check if user doesn't have a page - redirect to waitlist
      if (result.message === 'no_page' || result.redirect === 'waitlist') {
        toast.info("You're not on the list yet. Join the waitlist!");
        navigate('/waitlist');
        return;
      }
      
      // Handle admin bypass - direct login without magic link
      if (result.message === 'admin_bypass' && result.token) {
        // Store token using the same key as AuthContext
        localStorage.setItem('uncut_token', result.token);
        toast.success('Admin login successful!');
        // Force page reload to pick up the new token
        window.location.href = '/admin';
        return;
      }
      
      setSent(true);
      toast.success('Magic link sent! Check your email.');
      
      // For development: if magic_link is returned, show it
      if (result.magic_link) {
        console.log('Magic link:', result.magic_link);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center animate-slide-up">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl font-medium text-[#1A1918] mb-4">
            Check your email
          </h1>
          <p className="text-[#666460] mb-6">
            We sent a magic link to <strong>{email}</strong>. Click the link in the email to sign in.
          </p>
          <Button 
            variant="outline" 
            className="btn-secondary"
            onClick={() => setSent(false)}
          >
            Use a different email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#E6E4E0] bg-white">
        <div className="container-app flex items-center h-16">
          <Link to="/" className="flex items-center gap-2 text-[#666460] hover:text-[#1A1918]">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-slide-up">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-medium text-[#1A1918]">
              Sign in
            </h1>
          </div>

          <form onSubmit={handleSendLink} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 input-base"
                autoFocus
                data-testid="email-input"
              />
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="send-link-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send magic link
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-[#666460] mt-6">
            Don&apos;t have an account? Just enter your email and we&apos;ll create one for you.
          </p>
        </div>
      </main>
    </div>
  );
}
